import { readWorkflowStates } from "../query";
import { graphQlManager } from "../services/server";
import {
    Product,
    DesignCreationMessage,
    SelectedVariants,
    Transaction,
    DesignDetails,
    DesignWorkflowMetadata,
    ExportedData,
    FrameMetadata,
    IllustrationMetadata,
    ModuleMetadata,
    TextMetadata,
    Design,
    Workflow,
    Step,
    AnyStepData,
    FrameStepData,
    IllustrationStepData,
    ModuleStepData,
    TextStepData,
    ILayout,
    AssetType,
} from "../types";
import {
    WorkflowManager,
    DesignCreationProgressUpdate,
    WorkflowSelections,
    WorkflowMetadata,
} from "../WorkflowManager";
import { persistenceService } from "./persistence";
import { UnhandledBehaviorError } from "../util/exception";
import { gql } from "@apollo/client/core";
import { assetService } from "./asset";
import { CommandState } from "../CommandContext";
import { fetch } from "../util/crossplatform";

/**
 * This file contains all the functions that contribute to the createDesign function.
 */

const createDesignTransactionFragment = gql`
    fragment CreateDesignTransaction on Transaction {
        id
        designName
        externalCartProductId
        externalCartProductVariantId
        externalDesignProductId
        externalDesignProductVariantId
        lastSyncedAt
        quantity
        previewImageLink
        priceModifierTotal
        workflowViewerLink
        workflowViewerReadOnlyLink
        integrationProduct {
            id
            additionalExternalProductId
            additionalExternalVariantId
        }
        designExternalVariants {
            id
            externalProductId
            externalProductVariantId
        }
    }
`;

const createDesignMutation = gql`
    ${createDesignTransactionFragment}
    mutation CreateDesign(
        $name: String!
        $layouts: [LayoutInput]!
        $workflowId: String!
        $transactionId: String!
        $previewImage: String
        $useThreeDimPreview: Boolean
        $metadata: [MetadataInput]
        $selectedVariants: [SelectedVariantInput]
    ) {
        designCreate(
            name: $name
            layouts: $layouts
            workflowId: $workflowId
            transactionId: $transactionId
            previewImage: $previewImage
            useThreeDimPreview: $useThreeDimPreview
            metadata: $metadata
            selectedVariants: $selectedVariants
        ) {
            id
            sku
            transaction {
                ...CreateDesignTransaction
            }
            processExecution {
                id
            }
        }
    }
`;

const createDesignsMutation = gql`
    ${createDesignTransactionFragment}
    mutation CreateDesigns($inputs: [DesignCreateInput]!) {
        designCreateMany(inputs: $inputs) {
            id
            sku
            transaction {
                ...CreateDesignTransaction
            }
            processExecution {
                id
            }
        }
    }
`;

// https://stackoverflow.com/a/69058154
export function isTokenExpired(token: string) {
    const expiry = JSON.parse(atob(token.split(".")[1])).exp;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
}

const createDesignGqlCall = async (designDetails: DesignDetails): Promise<undefined | Design> => {
    // Hack to get the token from editor's amplify.
    const keys = Object.entries(localStorage);
    const jwtTokenKey =
        keys.find(([k, _]) => {
            return k.startsWith("CognitoIdentityServiceProvider") && k.endsWith("idToken");
        })?.[0] || "";
    const jwtToken = localStorage.getItem(jwtTokenKey);
    const extraHeaders: { [key: string]: string } = {};
    if (jwtToken && !isTokenExpired(jwtToken)) {
        extraHeaders["Authorization"] = `Bearer ${jwtToken}`;
    }

    const response = await graphQlManager.getShadowGraphqlClient().mutate<{ designCreate: Design }>({
        mutation: createDesignMutation,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            name: designDetails.name,
            layouts: designDetails.layouts,
            workflowId: designDetails.workflowId,
            transactionId: designDetails.transactionId,
            previewImage: designDetails.previewImage,
            useThreeDimPreview: designDetails.useThreeDimPreview,
            metadata: designDetails.metadata,
            selectedVariants: designDetails.selectedVariants,
        },
        context: {
            headers: extraHeaders,
        },
    });
    return response.data?.designCreate;
};

const createDesignsGqlCall = async (designDetails: DesignDetails[]): Promise<undefined | Design[]> => {
    // Hack to get the token from editor's amplify.
    const keys = Object.entries(localStorage);
    const jwtTokenKey =
        keys.find(([k, _]) => {
            return k.startsWith("CognitoIdentityServiceProvider") && k.endsWith("idToken");
        })?.[0] || "";
    const jwtToken = localStorage.getItem(jwtTokenKey);
    const extraHeaders: { [key: string]: string } = {};
    if (jwtToken && !isTokenExpired(jwtToken)) {
        extraHeaders["Authorization"] = `Bearer ${jwtToken}`;
    }

    const response = await graphQlManager.getShadowGraphqlClient().mutate<{ designCreateMany: Design[] }>({
        mutation: createDesignsMutation,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            inputs: designDetails.map((designDetail) => ({
                name: designDetail.name,
                layouts: designDetail.layouts,
                workflowId: designDetail.workflowId,
                transactionId: designDetail.transactionId,
                previewImage: designDetail.previewImage,
                useThreeDimPreview: designDetail.useThreeDimPreview,
                metadata: designDetail.metadata,
                selectedVariants: designDetail.selectedVariants,
            })),
        },
        context: {
            headers: extraHeaders,
        },
    });
    return response.data?.designCreateMany;
};

const formatMetadata = (
    metadata: WorkflowMetadata,
    workflow: Workflow,
    forCart: boolean,
): { [name: string]: string } => {
    const metadataObjectForStep = (step: Step<AnyStepData>) => {
        const formattedMetadata = {} as any;
        if (step.type === "Frame") {
            const stepMetadata = metadata[step.stepName] as FrameMetadata;
            const stepData = step.data as FrameStepData;
            if (!stepMetadata || !stepMetadata.image || (stepData.hideImageInCart && forCart)) {
                return formattedMetadata;
            }
            // TODO: Need to replace 'image' with locize key 'exportedData.image'
            formattedMetadata[`${step.stepTitle} ${"image"}`] = stepMetadata.image;
        }
        if (step.type === "Illustration") {
            const stepMetadata = metadata[step.stepName] as IllustrationMetadata;
            const stepData = step.data as IllustrationStepData;
            if (!stepMetadata || (stepData.hideColorsInCart && forCart) || !stepMetadata.colors) {
                return formattedMetadata;
            }
            if (stepMetadata.colors.length > 0) {
                const colorString = stepMetadata.colors.join(", ").toUpperCase();
                // TODO: Need to replace 'colors' with locize key 'exportedData.colors'
                formattedMetadata[`${step.stepTitle} ${"colors"}`] = colorString;
            }
        }
        if (step.type === "Module") {
            const stepMetadata = metadata[step.stepName] as ModuleMetadata;
            const stepData = step.data as ModuleStepData;
            if (
                !stepMetadata ||
                stepMetadata.text === undefined ||
                stepMetadata.text === null ||
                (stepData.hideTextInCart && forCart)
            ) {
                return formattedMetadata;
            }
            // TODO: Need to replace 'text' with locize key 'exportedData.text'
            formattedMetadata[`${step.stepTitle} ${"text"}`] = stepMetadata.text;
        }
        if (step.type === "Text") {
            const stepMetadata = metadata[step.stepName] as TextMetadata;
            if (!stepMetadata) {
                return formattedMetadata;
            }
            const stepData = step.data as TextStepData;
            if (
                !stepData.hideTextInCart ||
                (!forCart && stepMetadata.text !== undefined && stepMetadata.text !== null)
            ) {
                // TODO: Need to replace 'text' with locize key 'exportedData.text'
                formattedMetadata[`${step.stepTitle} ${"text"}`] = stepMetadata.text;
            }
            if (stepMetadata.color && (!stepData.hideColorInCart || !forCart)) {
                // TODO: Need to replace 'color' with locize key 'exportedData.color'
                formattedMetadata[`${step.stepTitle} ${"color"}`] = stepMetadata.color;
            }
            if (stepMetadata.strokeColor && (!stepData.hideStrokeInCart || !forCart)) {
                formattedMetadata[`${step.stepTitle} ${"stroke"}`] = stepMetadata.strokeColor;
            }
        }
        return formattedMetadata;
    };

    const formattedMetadata = {};
    workflow.steps.forEach((step) => {
        Object.assign(formattedMetadata, metadataObjectForStep(step));
    });
    return formattedMetadata;
};

// Combine selections and metadata into exported data.
// Items should be grouped by step in workflow order.
const prepareExportedData = (
    selections: SelectedVariants,
    workflow: Workflow,
    metadata?: WorkflowMetadata,
    designWorkflowMetadata?: DesignWorkflowMetadata,
): ExportedData => {
    const exportedData = {} as any;
    let formattedMetadata: DesignWorkflowMetadata = {};
    if (metadata) {
        formattedMetadata = formatMetadata(metadata, workflow, false);
    } else if (designWorkflowMetadata) {
        formattedMetadata = designWorkflowMetadata;
    }

    const stepTitles = workflow.steps.map((s) => s.stepTitle);
    // https://stackoverflow.com/a/39272981
    const distinctStepTitles = stepTitles.filter((x, i, a) => a.indexOf(x) == i);
    for (const stepTitle of distinctStepTitles) {
        if (Object.keys(selections).includes(stepTitle)) {
            const selection = selections[stepTitle];
            const step = workflow.steps.find((step) => step.stepTitle === stepTitle);
            // TODO: Need to replace 'selection' with locize key 'exportedData.selection'
            exportedData[`${step?.stepTitle} ${"selection"}`] = {
                value: selection[0].name,
                priceModifier: selection[0].priceModifier,
            };
        }
        for (const key of Object.keys(formattedMetadata)) {
            if (
                [`${stepTitle} color`, `${stepTitle} colors`, `${stepTitle} text`, `${stepTitle} image`].includes(key)
            ) {
                exportedData[key] = {
                    value: formattedMetadata[key],
                    priceModifier: 0,
                };
            }
        }
    }

    return exportedData;
};

const createDesignMessage = (
    transaction: Transaction,
    product: Product,
    workflow: Workflow,
    selectedVariants: SelectedVariants,
    generatedSku?: string,
    metadata?: { [stepName: string]: string },
    lineItemImageLink?: string,
    processExecutionId?: string,
): DesignCreationMessage => {
    const baseCost = product.basePrice || 0;
    const optionsCost = transaction.priceModifierTotal || 0;
    const exportedData = prepareExportedData(selectedVariants, workflow, undefined, metadata);

    const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
    const ownerId = ownerMap.get(transaction.id);

    const msgObject: DesignCreationMessage = {
        additionalExternalProductId: transaction.integrationProduct?.additionalExternalProductId,
        additionalExternalVariantId: transaction.integrationProduct?.additionalExternalVariantId,
        baseCost,
        designExternalVariants: transaction.designExternalVariants,
        designProductId: transaction.externalDesignProductId,
        designProductVariantId: transaction.externalDesignProductVariantId,
        event: "onComplete",
        exportedData,
        externalCartProductId: transaction.externalCartProductId,
        externalCartProductVariantId: transaction.externalCartProductVariantId,
        lineItemImageUrl: lineItemImageLink || "",
        optionsCost,
        processExecutionId,
        quantity: transaction.quantity,
        transactionId: transaction.id,
        transactionOwnerId: ownerId,
        weight: product.weight,
        workflowViewerLink: transaction.workflowViewerLink || "",
        workflowViewerReadOnlyLink: transaction.workflowViewerReadOnlyLink || "",
    };
    if (metadata) {
        msgObject.metadata = metadata;
    }
    if (selectedVariants) {
        msgObject.selectedVariants = selectedVariants;
    }
    if (generatedSku) {
        msgObject.sku = generatedSku;
    }
    return msgObject;
};

export const createDesignDetails = async (
    workflowManager: WorkflowManager,
    workflow: Workflow,
    layouts: ILayout[],
    getReducerState: () => CommandState,
    transaction: Transaction,
    workflowSelections: WorkflowSelections,
    designName: string,
    createPreviewImage: (shouldRender3D?: boolean, transactionId?: string) => Promise<string | undefined>,
    workflowMetadata?: WorkflowMetadata,
    serverState?: string | null,
): Promise<{
    designDetails: DesignDetails;
    cartSelectionsWithPrices: SelectedVariants;
    cartMetadata: { [stepName: string]: string } | undefined;
}> => {
    const flushAndGetServerState = async (): Promise<string | null> => {
        if (serverState !== undefined) return serverState;
        // Here we resolve any remaining state frames.
        // After doing so we make a call to the server and pull
        // the servers known state to validate synchronization.
        // FIXME: This currently only waits for any server requests to complete. If there are any debounced requests
        // that are still pending, they will not be waited on.
        await workflowManager.outstandingRequestsPromise();
        const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
        const ownerId = ownerMap.get(transaction.id);
        const graphQlClient = graphQlManager.getShadowGraphqlClient();
        const serverWorkflowState = await graphQlClient.query<{
            transactions: Transaction[];
        }>({
            query: readWorkflowStates,
            variables: { ids: [transaction.id] },
            fetchPolicy: "no-cache",
            errorPolicy: "all",
            context: {
                transactionOwnerId: ownerId,
            },
        });

        const state = serverWorkflowState.data?.transactions[0].workflowState;
        if (serverWorkflowState.errors) {
            serverWorkflowState.errors.forEach((e) => {
                serverWorkflowState.errors && console.log("Server Error:", e.message);
            });
            return null;
        }
        return state ?? null;
    };
    const state = await flushAndGetServerState();
    if (!state) {
        console.warn("State mismatch detected. Uploading known state explicitly");
        console.warn("State Object:", JSON.stringify(getReducerState()));
        await workflowManager.updateStateWithServerImmediate(getReducerState);
        console.log("Server state is undefined @ Workflow completion"!);
    }

    const previewService = workflowManager.getPreviewService();
    const cameraOrientation = workflow?.finalizeStepConfig?.lookAtAnimation;
    const shouldUploadThreeDimPreview = previewService && workflow.showModelOnFinishStep && !!cameraOrientation;

    const cartMetadata = workflowMetadata && formatMetadata(workflowMetadata, workflow, true);
    const designMetadata = workflowMetadata && formatMetadata(workflowMetadata, workflow, false);

    const getSelections = async (forCart: boolean): Promise<[SelectedVariants, number]> => {
        const selectedVariants: SelectedVariants = {};
        let optionsCost = 0;
        if (Object.keys(workflowSelections).length > 0) {
            for (const stepId of Object.keys(workflowSelections)) {
                const stepSelections = workflowSelections[stepId];
                const step = workflow.steps.find((step) => step.stepName === stepId);
                for (let i = 0; i < stepSelections.selections.length; ++i) {
                    const variant = stepSelections.selections[i];
                    // Attach a selection if:
                    // * these selections aren't for the cart; or
                    // * the selection isn't configured to be hidden and there's
                    // multiple variants for the step.
                    if (
                        step &&
                        (!forCart ||
                            (step.option &&
                                (step.option.variants || []).length > 1 &&
                                !(step.data as any).hideSelectionInCart &&
                                !(step.data as any).hideSelectionsInCart))
                    ) {
                        const stepTitle = step.stepTitle;
                        if (selectedVariants[stepTitle]) {
                            selectedVariants[stepTitle].push({
                                id: variant.id || "",
                                name: variant.name,
                                priceModifier: variant.priceModifier,
                            });
                        } else {
                            selectedVariants[stepTitle] = [
                                {
                                    id: variant.id || "",
                                    name: variant.name,
                                    priceModifier: variant.priceModifier,
                                },
                            ];
                        }
                    }
                    optionsCost += variant.priceModifier;
                }
            }
        }
        return [selectedVariants, optionsCost];
    };
    const [cartSelectionsWithPrices] = await getSelections(true);
    const cartSelections: { [key: string]: string[] } = Object.fromEntries(
        Object.keys(cartSelectionsWithPrices).map((key) => [key, cartSelectionsWithPrices[key].map((item) => item.id)]),
    );
    const [designSelectionsWithPrices] = await getSelections(false);
    const designSelections: { [key: string]: string[] } = Object.fromEntries(
        Object.keys(designSelectionsWithPrices).map((key) => [
            key,
            designSelectionsWithPrices[key].map((item) => item.id),
        ]),
    );

    // We are required to upload some assets now, we'll store their upload promises here
    // and await them at the end so we can get through as much computation as possible without
    // holding up the main thread.
    let previewImageUrl = await createPreviewImage(shouldUploadThreeDimPreview, transaction.id);
    if (previewImageUrl) {
        const previewImageBlob = await (await fetch(previewImageUrl)).blob();
        const asset = await assetService.uploadAsset(
            { name: `${transaction.id}-preview-image.png`, blob: previewImageBlob },
            AssetType.Image,
            true,
            false,
        );
        previewImageUrl = asset.fileLink!;
    }

    const assembleDesignDetails = () => {
        const designDetails: DesignDetails = {
            name: designName,
            layouts: layouts.map((layout) => {
                return {
                    index: layout.index,
                    panelId: layout.panelId,
                };
            }),
            workflowId: workflow.id,
            transactionId: transaction.id,
            useThreeDimPreview: !!shouldUploadThreeDimPreview,
            previewImage: previewImageUrl,
        };
        if (designMetadata) {
            const metadataList: { key: string; value: string }[] = [];
            for (const [key, value] of Object.entries(designMetadata)) {
                metadataList.push({ key, value });
            }
            designDetails.metadata = metadataList;
        }
        if (cartSelections) {
            const selectionsList: { key: string; ids: string[] }[] = [];
            for (const [key, value] of Object.entries(designSelections)) {
                selectionsList.push({ key, ids: value });
            }
            designDetails.selectedVariants = selectionsList;
        }
        return designDetails;
    };
    return {
        designDetails: assembleDesignDetails(),
        cartSelectionsWithPrices,
        cartMetadata,
    };
};

export const createDesign = async (
    workflowManager: WorkflowManager,
    workflow: Workflow,
    layouts: ILayout[],
    getReducerState: () => CommandState,
    product: Product,
    transaction: Transaction,
    workflowSelections: WorkflowSelections,
    designName: string,
    onProgressUpdate: DesignCreationProgressUpdate,
    createPreviewImage: (shouldRender3D?: boolean, transactionId?: string) => Promise<string | undefined>,
    workflowMetadata?: WorkflowMetadata,
): Promise<DesignCreationMessage> => {
    onProgressUpdate("workflow.steps.finish.finalize.buildingLayouts");

    // TODO: Since we have mandatory steps and field validation, should we validate here or keep relying on the theme to do this?

    const { designDetails, cartSelectionsWithPrices, cartMetadata } = await createDesignDetails(
        workflowManager,
        workflow,
        layouts,
        getReducerState,
        transaction,
        workflowSelections,
        designName,
        createPreviewImage,
        workflowMetadata,
    );

    onProgressUpdate("workflow.steps.finish.finalize.creatingDesign");

    const createDesignResponse = await createDesignGqlCall(designDetails);
    const lineItemImageLink = createDesignResponse?.transaction?.previewImageLink;

    if (!createDesignResponse?.transaction) {
        throw new Error("Failed to create design");
    }

    // The design has been created at this point. We know that the transaction will now contain
    // the external product id from shopify for this specific order if relevant.
    onProgressUpdate("workflow.steps.finish.finalize.updatingTransaction");
    const upToDateTransaction = createDesignResponse.transaction;
    return createDesignMessage(
        upToDateTransaction,
        product,
        workflow,
        cartSelectionsWithPrices,
        createDesignResponse?.sku,
        cartMetadata,
        lineItemImageLink,
        createDesignResponse?.processExecution?.id,
    );
};

export const createDesigns = async (
    options: {
        workflowManager: WorkflowManager;
        workflow: Workflow;
        layouts: ILayout[];
        getReducerState: () => CommandState;
        product: Product;
        transaction: Transaction;
        workflowSelections: WorkflowSelections;
        designName: string;
        workflowMetadata?: WorkflowMetadata;
    }[],
    onProgressUpdate: DesignCreationProgressUpdate,
    createPreviewImage?: (shouldRender3D?: boolean, transactionId?: string) => Promise<string | undefined>,
): Promise<DesignCreationMessage[]> => {
    onProgressUpdate("workflow.steps.finish.finalize.buildingLayouts");

    await Promise.all(
        options.map(async (option) => {
            await option.workflowManager.outstandingRequestsPromise();
        }),
    );

    const getWorkflowStates = async (): Promise<(string | null)[] | null> => {
        const graphQlClient = graphQlManager.getShadowGraphqlClient();
        const serverWorkflowStates = await graphQlClient.query<{
            transactions: Transaction[];
        }>({
            query: readWorkflowStates,
            variables: { ids: options.map((option) => option.transaction.id) },
            fetchPolicy: "no-cache",
            errorPolicy: "all",
        });

        if (serverWorkflowStates.errors) {
            serverWorkflowStates.errors.forEach((e) => {
                serverWorkflowStates.errors && console.log("Server Error:", e.message);
            });
            return null;
        }
        if (!serverWorkflowStates.data?.transactions) {
            return null;
        }
        return serverWorkflowStates.data.transactions.map((transaction) => transaction.workflowState ?? null);
    };
    const states = await getWorkflowStates();

    const createDesignResults = await Promise.all(
        options.map(async (option, index) => {
            return await createDesignDetails(
                option.workflowManager,
                option.workflow,
                option.layouts,
                option.getReducerState,
                option.transaction,
                option.workflowSelections,
                option.designName,
                createPreviewImage ?? (() => Promise.resolve(undefined)),
                option.workflowMetadata,
                states ? states[index] : undefined,
            );
        }),
    );

    onProgressUpdate("workflow.steps.finish.finalize.creatingDesign");

    const createDesignsResponse = await createDesignsGqlCall(createDesignResults.map((result) => result.designDetails));
    if (!createDesignsResponse) {
        throw new Error("Failed to create designs");
    }
    onProgressUpdate("workflow.steps.finish.finalize.updatingTransaction");
    return createDesignsResponse.map((design, index) => {
        const option = options[index];
        const createDesignResult = createDesignResults[index];
        const upToDateTransaction = design.transaction!;
        return createDesignMessage(
            upToDateTransaction,
            option.product,
            option.workflow,
            createDesignResult.cartSelectionsWithPrices,
            design.sku,
            createDesignResult.cartMetadata,
            upToDateTransaction.previewImageLink,
            design.processExecution?.id,
        );
    });
};

export interface SavedDesign {
    /**
     * The user's name for this saved design.
     */
    title: string;
    /**
     * A URL pointing to an image of the design. Typically a data URL
     */
    thumbnail?: string;
    /**
     * The ID of the transaction relating to this design.
     */
    transactionId: string;
    /**
     * The product ID for this transaction.
     */
    productId: string;
    /**
     * The integration product ID related to this order.
     */
    integrationProductId: string;
    /**
     * The name of the workflow annotated at time of save (may be different from current workflow name).
     */
    workflowName: string;
    /**
     * The ID of the workflow annotated at time of save.
     */
    workflowId: string;
    /**
     * The last edit that occured on this saved design.
     */
    lastEdited: Date;
}
/**
 * Represents the current method of storage for saved designs.
 */
enum StorageMethod {
    Local = "Local",
    Remote = "Remote",
}

/**
 * The design service exposes helper functionality wrapping important design management operations.
 * NOTE: In the future this interface should allow for storing/pulling designs from the server.
 */
class DesignService {
    public readonly localPersistenceKey = "designTransactions";
    private storageMethod: StorageMethod = StorageMethod.Local;
    private designSavedListeners: ((designs: SavedDesign) => void)[] = [];

    /**
     * @param func The function to call when a design is saved.
     */
    attachSaveListener(func: (design: SavedDesign) => void) {
        this.designSavedListeners.push(func);
    }

    /**
     * @param func The function to remove from the list of listeners.
     */
    detachSaveListener(func: (design: SavedDesign) => void) {
        this.designSavedListeners = this.designSavedListeners.filter((f) => f !== func);
    }

    /**
     * Gets the currently persisted designs.
     */
    async getSavedDesigns(): Promise<SavedDesign[]> {
        if (this.storageMethod === StorageMethod.Local) {
            const persistedString = persistenceService.get(this.localPersistenceKey);
            const savedDesigns = persistedString ? (JSON.parse(persistedString) as SavedDesign[]) : [];
            return savedDesigns;
        }
        throw new UnhandledBehaviorError("Unexpected storage method requested");
    }

    /**
     * Search for a transaction that has been saved.
     * @param transactionId The id to search for.
     * @returns The transaction for the given id provided it has been saved. undefined if it doesn't exist.
     */
    async getSavedDesignByTransaction(transactionId: string): Promise<SavedDesign | undefined> {
        const designs = await this.getSavedDesigns();
        return designs.find((d) => d.transactionId === transactionId);
    }

    /**
     * Saves a design to storage.
     * @param design The design to save.
     */
    async addDesign(design: SavedDesign) {
        const designs = await this.getSavedDesigns();
        const newDesigns = designs.filter((d) => d.transactionId !== design.transactionId);
        newDesigns.unshift(design);
        await this.setDesigns(newDesigns);
        this.designSavedListeners.forEach((listener) => listener(design));
    }

    /**
     * Change the user's name of the given saved design.
     */
    async renameDesign(transactionId: string, title: string) {
        const designs = await this.getSavedDesigns();
        const design = designs.find((d) => d.transactionId === transactionId);
        if (!design) {
            throw new Error(`No saved design for transaction ${transactionId}.`);
        }
        design.title = title;
        await this.setDesigns(designs);
    }

    /**
     * Removes a given design from storage.
     * @param transactionId
     */
    async removeDesign(transactionId: string) {
        const designs = await this.getSavedDesigns();
        await this.setDesigns(designs.filter((d) => d.transactionId !== transactionId));
    }

    private async setDesigns(designs: SavedDesign[]) {
        if (this.storageMethod === StorageMethod.Local) {
            persistenceService.set(this.localPersistenceKey, JSON.stringify(designs));
            return;
        }
        throw new UnhandledBehaviorError("Unexpected storage method requested");
    }
}
const designService = new DesignService();
export { designService };
